import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import translate from '../../../commonComponents/translate';
import CategoryProp from '../../academy/propTypes/categoryProp';
import CountryProp from '../../../commonComponents/forms/Address/CountryProp';
import CurrencyProp from '../../profile/propTypes/currencyProp';
import AccessSessionProp from '../../../commonComponents/propTypes/accessSessionProp';
import PromoDates from '../../../commonComponents/promoDates/PromoDates';

const statusList = t => [
  {
    id: 'draft',
    text: t('access_session.list.status.draft'),
  },
  {
    id: 'lead',
    text: t('access_session.list.status.lead'),
  },
  {
    id: 'planned',
    text: t('access_session.list.status.planned'),
  },
  {
    id: 'confirmed',
    text: t('access_session.list.status.confirmed'),
  },
  {
    id: 'canceled',
    text: t('access_session.list.status.canceled'),
  },
  {
    id: 'closed',
    text: t('access_session.list.status.closed'),
  },
];

function TabInformation({
  t,
  select,
  createInput,
  createCustomHTMLEditor,
  createImage,
  categories,
  languages,
  currencies,
  countries,
  isFinished,
  isStatusFinished,
  status,
  createSelectAvailableCountries,
  createPromoDateTime,
  session,
  organizationVatRates,
  isPaymentProviderNone,
}) {
  const { vatRates: orgVatRates, vatRatesCategories: orgVatRatesCategories } =
    organizationVatRates;
  const dataStatus = useMemo(() => {
    const statuses = [];
    if (!isFinished) {
      statuses.push(
        statuses.push(statusList(t).find(s => s.id === 'draft')),
        statuses.push(statusList(t).find(s => s.id === 'lead')),
        statuses.push(statusList(t).find(s => s.id === 'planned')),
        statuses.push(statusList(t).find(s => s.id === 'confirmed')),
      );
    }
    if (status !== 'closed' && !isStatusFinished) {
      statuses.push(statusList(t).find(s => s.id === 'canceled'));
    }
    if (isStatusFinished) {
      statuses.push(
        statusList(t).find(s => s.id === status),
        statusList(t).find(s => s.id === 'closed'),
      );
    }
    return statuses;
  }, [isFinished, status]);

  const modes = [
    {
      id: 'private',
      text: t('private'),
    },
    {
      id: 'public',
      text: t('public'),
    },
  ];

  const vatRates = useMemo(() => {
    let rates = [];
    const isCategoryOther = session?.vatRateCategory === 'other';
    // If the category is other we should show the normal vatRates
    if (orgVatRates?.length > 0) {
      rates = orgVatRates
        .map(v => ({
          id: v.type,
          text: isCategoryOther
            ? `${t(`product.variation.vatRate.${v?.type}`)} (${v?.percentage}%)`
            : `${v?.percentage}%`,
          percentage: v.percentage,
        }))
        ?.sort((a, b) => a.percentage - b.percentage);
    }
    return rates;
  }, [orgVatRates, session.vatRateCategory, session.vatRate]);

  const ratesCategoriesList = useMemo(() => {
    let rates = [];
    if (orgVatRatesCategories) {
      const orgRatesCategories = Object.keys(orgVatRatesCategories).map(
        key => ({ type: key, percentage: orgVatRatesCategories[key] }),
      );
      if (orgRatesCategories?.length > 0) {
        rates = [
          ...orgRatesCategories
            .map(v => ({
              id: v.type,
              text: t(`organization.countries.vats.category.${v.type}`),
            }))
            ?.sort((a, b) => a.id.localeCompare(b.id)),
          {
            id: 'other',
            text: t('organization.countries.vats.category.otherCategory'),
          },
        ];
      }
    } else {
      rates = [
        {
          id: 'other',
          text: t('organization.countries.vats.category.otherCategory'),
        },
      ];
    }
    return rates;
  }, [organizationVatRates.vatRatesCategories]);
  const hintVatRate =
    session?.vatRateCategory && session?.vatRateCategory !== 'other'
      ? t(
          `organization.countries.vats.category.${session.vatRateCategory}.hint`,
        )
      : '';

  return (
    <>
      {select(
        'mode',
        modes.map(c => ({ id: c.id, text: c.text })),
        'col-lg-3 col-12 session-label-align',
        'col-lg-6 col-12',
      )}
      {select(
        'status',
        dataStatus.map(c => ({ id: c.id, text: c.text })),
        'col-lg-3 col-12 session-label-align',
        'col-lg-6 col-12',
      )}
      {createInput(
        'name',
        'text',
        true,
        'col-lg-3 col-12 session-label-align',
        'col-lg-6 col-12',
      )}
      {createInput(
        'summary',
        'text',
        false,
        'col-lg-3 col-12 session-label-align',
        'col-lg-6 col-12',
      )}
      {createCustomHTMLEditor(
        'description',
        'text',
        false,
        'col-lg-3 col-12 session-label-align',
        'col-lg-6 col-12',
      )}
      {select(
        'category',
        categories.map(c => ({ id: c._id, text: c.name })),
        'col-lg-3 col-12 session-label-align',
        'col-lg-6 col-12',
      )}
      {createImage(
        'sessionImage',
        'imageContainer',
        'col-lg-3 col-12 col-form-label session-label-align',
        '1:1',
      )}
      {select(
        'language',
        languages.map(c => ({ id: c.code, text: c.name })),
        'col-lg-3 col-12 session-label-align',
        'col-lg-6 col-12',
      )}
      {createSelectAvailableCountries(
        'availableCountries',
        countries.map(c => ({ id: c.code, text: c.name })),
        false,
        'col-lg-3 col-12 session-label-align',
        'col-lg-6 col-12',
        false,
      )}
      {select(
        'currency',
        currencies.map(c => ({ id: c.code, text: c.name })),
        'col-lg-3 col-12 session-label-align',
        'col-lg-6 col-12',
      )}
      {createInput(
        'contentAvailableBeforePayment',
        'checkbox',
        false,
        'col-lg-3 col-10 session-label-align',
        'col-lg-6 col-2 content-available-before-payment-container',
        undefined,
        undefined,
        true,
      )}
      {createInput(
        'price',
        'price',
        false,
        'col-lg-3 col-12 session-label-align',
        'col-lg-6 col-12',
        undefined,
        undefined,
        true,
      )}
      {createInput(
        'promotionsPrice',
        'price',
        false,
        'col-lg-3 col-12 session-label-align',
        'col-lg-6 col-12',
        undefined,
        undefined,
        true,
      )}
      <PromoDates
        createPromoDateTime={createPromoDateTime}
        promotionPriceDateStart={session.promotionPriceDateStart}
        promotionPriceDateEnd={session.promotionPriceDateEnd}
      />
      {!isPaymentProviderNone && (
        <div className="row">
          <label
            htmlFor="vatRateCategory"
            className="col-lg-3 col-12 session-label-align col-form-label label-selector"
          >
            {t('access_session.detail.vatRateCategory')}
          </label>

          <div className="col-lg-6 col-12">
            <div className="row">
              <div className="col-lg-5 col-12">
                {select(
                  'vatRateCategory',
                  ratesCategoriesList.map(c => ({ id: c.id, text: c.text })),
                  'col-12 text-left',
                  'col-12',
                  {
                    searchable: false,
                    allowClear: false,
                  },
                  false,
                )}
              </div>
              <div className="col-lg-7 col-12">
                {select(
                  'vatRate',
                  vatRates.map(c => ({ id: c.id, text: c.text })),
                  'col-lg-3 col-12 text-left',
                  'col-lg-9 col-12',
                  {
                    searchable: false,
                    allowClear: false,
                  },
                  true,
                  hintVatRate,
                  session?.vatRateCategory !== 'other',
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

TabInformation.propTypes = {
  t: PropTypes.func.isRequired,
  createInput: PropTypes.func.isRequired,
  createCustomHTMLEditor: PropTypes.func.isRequired,
  select: PropTypes.func.isRequired,
  isFinished: PropTypes.bool.isRequired,
  isStatusFinished: PropTypes.bool.isRequired,
  createSelectAvailableCountries: PropTypes.func.isRequired,
  createImage: PropTypes.func.isRequired,
  categories: PropTypes.arrayOf(PropTypes.shape(CategoryProp.propType))
    .isRequired,
  languages: PropTypes.arrayOf(PropTypes.shape(CountryProp.propType))
    .isRequired,
  currencies: PropTypes.arrayOf(PropTypes.shape(CurrencyProp.propType))
    .isRequired,
  countries: PropTypes.arrayOf(PropTypes.shape(CountryProp.propType))
    .isRequired,
  status: PropTypes.string.isRequired,
  session: PropTypes.shape(AccessSessionProp.propType).isRequired,
  organizationVatRates: PropTypes.shape({
    country: PropTypes.string,
    isDefault: PropTypes.bool,
    vatRates: PropTypes.arrayOf(
      PropTypes.shape({
        percentage: PropTypes.number,
        type: PropTypes.string,
      }),
    ),
    vatRatesCategories: PropTypes.shape({
      accommodation: PropTypes.number,
      'admission to cultural events': PropTypes.number,
      'admission to entertainment events': PropTypes.number,
      'admission to sporting events': PropTypes.number,
      advertising: PropTypes.number,
      'agricultural supplies': PropTypes.number,
      'baby foodstuffs': PropTypes.number,
      bikes: PropTypes.number,
      books: PropTypes.number,
      'childrens clothing': PropTypes.number,
      'domestic fuel': PropTypes.number,
      'domestic services': PropTypes.number,
      ebooks: PropTypes.number,
      electricity: PropTypes.number,
      foodstuffs: PropTypes.number,
      hotels: PropTypes.number,
      medical: PropTypes.number,
      newspapers: PropTypes.number,
      'passenger transport': PropTypes.number,
      pharmaceuticals: PropTypes.number,
      'property renovations': PropTypes.number,
      restaurants: PropTypes.number,
      'social housing': PropTypes.number,
      water: PropTypes.number,
      wine: PropTypes.number,
    }),
    _id: PropTypes.string,
  }),
  isPaymentProviderNone: PropTypes.bool.isRequired,
  createPromoDateTime: PropTypes.func.isRequired,
};

TabInformation.defaultProps = {
  organizationVatRates: {
    vatRates: [],
    vatRatesCategories: {},
  },
};

export default translate(TabInformation);
